import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLogin } from "@/api/auth";
import { AgencyLogo } from "@/components/agency-logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CLIENT_ROLES } from "@/lib/roles";

export function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState<string | null>(null);
  const login = useLogin();
  const navigate = useNavigate();

  useEffect(() => {
    const error = localStorage.getItem("auth_error");
    if (error === "account_inactive") {
      setAuthError("Your account has been deactivated. Contact your administrator.");
      localStorage.removeItem("auth_error");
    }
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    login.mutate(
      { email, password },
      {
        onSuccess: (data) => {
          try {
            const payload = JSON.parse(atob(data.access.split(".")[1]));
            if (
              (CLIENT_ROLES as readonly string[]).includes(payload.role) &&
              payload.tenant_ids?.length
            ) {
              navigate(`/tenants/${payload.tenant_ids[0]}`);
              return;
            }
          } catch {
            // fall through to default redirect
          }
          navigate("/");
        },
      }
    );
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-background overflow-hidden">
      <div className="login-grid" />
      <div className="login-glow" />

      <div className="relative z-10 w-full max-w-sm rounded-2xl border border-border/60 bg-surface/90 p-8 shadow-elevated backdrop-blur-sm">
        <div className="mb-10 flex flex-col items-center gap-4">
          <AgencyLogo className="h-8 text-text-primary" />
          <p className="text-[10px] font-medium uppercase tracking-[0.25em] text-text-muted">
            Security Operations
          </p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-text-muted">
              Email
            </label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              required
            />
          </div>
          <div>
            <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-text-muted">
              Password
            </label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              required
            />
          </div>
          {authError && (
            <p className="text-sm text-danger">{authError}</p>
          )}
          {login.isError && (
            <p className="text-sm text-danger">Invalid credentials. Please try again.</p>
          )}
          <Button type="submit" className="w-full" disabled={login.isPending}>
            {login.isPending ? "Signing in..." : "Sign In"}
          </Button>
          <div className="text-center">
            <Link to="/forgot-password" className="text-sm text-text-muted hover:text-text-primary">
              Forgot password?
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
